import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Search, Building, ClipboardCheck, Loader2, ShieldCheck } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DEFAULT_DEPTS = [
  "Nursing","ICU","OT","Emergency","Laboratory","Pharmacy","Radiology","Quality",
  "Infection Control","HR","Administration","Housekeeping","Biomedical Engineering",
  "CSSD","Dietary","Front Office","Billing","MRD",
];

export default function OrgDepartmentsPage() {
  const [depts, setDepts] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({ department_name: "", department_code: "", description: "", status: "active" });
  const [saving, setSaving] = useState(false);
  const [headDlg, setHeadDlg] = useState(null); // department
  const [staffChoices, setStaffChoices] = useState([]);
  const [headUserId, setHeadUserId] = useState("");
  const [assigningHead, setAssigningHead] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/org/departments", { params });
      setDepts(data.data.departments);
    } finally { setLoading(false); }
  }, [search, status]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ department_name: "", department_code: "", description: "", status: "active" });
    setDlg({ create: true });
  };
  const openEdit = (d) => {
    setForm({ department_name: d.department_name, department_code: d.department_code, description: d.description || "", status: d.status });
    setDlg(d);
  };
  const submit = async () => {
    setSaving(true);
    try {
      if (dlg?.create) {
        await api.post("/org/departments", form);
        toast.success("Department created.");
      } else {
        await api.put(`/org/departments/${dlg.id}`, form);
        toast.success("Department updated.");
      }
      setDlg(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (d) => {
    try {
      await api.patch(`/org/departments/${d.id}/status`,
        { status: d.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const seedDefaults = async () => {
    const codes = new Set(depts.map((d) => d.department_code));
    let created = 0;
    for (const name of DEFAULT_DEPTS) {
      const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, "_");
      if (codes.has(code)) continue;
      try {
        await api.post("/org/departments", { department_name: name, department_code: code, status: "active" });
        created++;
      } catch { /* skip clashes */ }
    }
    toast.success(`${created} departments created.`);
    load();
  };

  const openAssignHead = async (d) => {
    setHeadDlg(d); setHeadUserId(d.department_head_user_id || "");
    try {
      const { data } = await api.get("/org/staff", { params: { page: 1, page_size: 200 } });
      setStaffChoices(data.data.users);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitAssignHead = async () => {
    if (!headDlg || !headUserId) return toast.error("Select a staff member.");
    setAssigningHead(true);
    try {
      await api.post(`/org/departments/${headDlg.id}/assign-head`, { user_id: headUserId });
      toast.success("Department head assigned.");
      setHeadDlg(null); setHeadUserId(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAssigningHead(false); }
  };

  return (
    <DashboardLayout title="Departments" breadcrumbs={["My Institute", "Departments"]}>
      <div className="space-y-4" data-testid="org-departments-page">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Search by name or code" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              {depts.length === 0 && (
                <button onClick={seedDefaults} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#329112]/40 bg-[#329112]/5 px-3 py-1.5 text-xs font-semibold text-[#329112]">
                  <ClipboardCheck className="h-3.5 w-3.5" /> Seed 18 healthcare departments
                </button>
              )}
              <button onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid="org-dept-add-btn">
                <Plus className="h-3.5 w-3.5" /> Add department
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading departments…" /> : depts.length === 0 ? (
            <EmptyState title="No departments yet" description="Create departments manually or seed the default healthcare set." icon={Building} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Code</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {depts.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-semibold text-slate-900">{d.department_name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{d.department_code}</TableCell>
                    <TableCell className="text-sm text-slate-600">{d.description || "—"}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openAssignHead(d)} className="inline-flex h-7 items-center justify-center rounded-md px-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-[#101876]" data-testid={`org-dept-assign-head-${d.id}`}>
                          <ShieldCheck className="mr-1 h-3.5 w-3.5" /> {d.department_head_user_id ? "Change head" : "Assign head"}
                        </button>
                        <button onClick={() => openEdit(d)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]"><Edit3 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => toggle(d)} className="inline-flex h-7 items-center justify-center rounded-md px-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-[#101876]">
                          {d.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{dlg?.create ? "Add department" : `Edit ${dlg?.department_name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Name</Label><Input value={form.department_name} onChange={(e) => setForm({ ...form, department_name: e.target.value })} data-testid="org-dept-name" /></div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Code</Label><Input value={form.department_code} onChange={(e) => setForm({ ...form, department_code: e.target.value })} data-testid="org-dept-code" /></div>
            <div className="space-y-1"><Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="org-dept-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {dlg?.create ? "Create" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!headDlg} onOpenChange={(o) => !o && setHeadDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid="org-dept-assign-head-dialog">
          <DialogHeader><DialogTitle>Assign head — {headDlg?.department_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Select staff (will be set as Department Head)</Label>
            <Select value={headUserId} onValueChange={setHeadUserId}>
              <SelectTrigger data-testid="org-dept-head-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
              <SelectContent>
                {staffChoices.length === 0 && <SelectItem value="none" disabled>No staff yet — add staff first</SelectItem>}
                {staffChoices.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <button onClick={() => setHeadDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitAssignHead} disabled={assigningHead || !headUserId} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="org-dept-head-submit">
              {assigningHead ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Assign
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
